/**
 * Social attribution — remembering which shared link brought a visitor.
 *
 * Every share button mints a short link (`/api/go/<code>`). Following it
 * lands on our page with `?src=<platform>&ref=<code>` appended. The code
 * is kept here for 30 days and sent as `ref` with the public forms
 * (agreement signup, appointment, contact, open-house sign-in), where the
 * backend records a conversion against the link. A stale or unknown code
 * is ignored server-side, so sending one can never break a form.
 */

export const REF_STORAGE_KEY = 'rews_ref';
export const REF_QUERY_PARAM = 'ref';
export const SRC_QUERY_PARAM = 'src';

const DAY_MS = 24 * 60 * 60 * 1000;
export const REF_TTL_MS = 30 * DAY_MS;

/**
 * Short-link codes are exactly 8 letters and digits, and the backend
 * ignores any other length. `?ref=` is also the affiliate referral
 * parameter (`REF-SHAY-XXXXXX`, minted on the Affiliates page); a code
 * of that shape is not attribution and must not replace a stored one.
 */
const REF_CODE_PATTERN = /^[A-Za-z0-9]{8}$/;
const SRC_MAX_LENGTH = 32;

export interface StoredAttribution {
    /** The short-link code. */
    ref: string;
    /** The platform the link was shared to, when the URL carried one. */
    src?: string;
    /** When it was captured (epoch ms); the entry expires REF_TTL_MS later. */
    at: number;
}

function readStorage(): StoredAttribution | null {
    try {
        const raw = window.localStorage.getItem(REF_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<StoredAttribution>;
        if (typeof parsed.ref !== 'string' || typeof parsed.at !== 'number') return null;
        return { ref: parsed.ref, src: parsed.src, at: parsed.at };
    } catch {
        // Private mode, blocked storage or corrupt JSON — behave as if nothing was stored.
        return null;
    }
}

function writeStorage(entry: StoredAttribution | null): void {
    try {
        if (entry) window.localStorage.setItem(REF_STORAGE_KEY, JSON.stringify(entry));
        else window.localStorage.removeItem(REF_STORAGE_KEY);
    } catch {
        // Storage unavailable — the visit is simply unattributed.
    }
}

/** The `ref` (and `src`) a URL's query string carries, when the code looks valid. */
export function readAttributionFromSearch(search: string): Pick<StoredAttribution, 'ref' | 'src'> | null {
    const params = new URLSearchParams(search);
    const ref = (params.get(REF_QUERY_PARAM) ?? '').trim();
    if (!REF_CODE_PATTERN.test(ref)) return null;
    const src = (params.get(SRC_QUERY_PARAM) ?? '').trim().slice(0, SRC_MAX_LENGTH);
    return src ? { ref, src } : { ref };
}

/**
 * Store the code from the current URL. Called once on app load; a
 * later link with a different code replaces the earlier one.
 */
export function captureAttribution(search = window.location.search, now = Date.now()): StoredAttribution | null {
    const found = readAttributionFromSearch(search);
    if (!found) return getAttribution(now);
    const entry: StoredAttribution = { ...found, at: now };
    writeStorage(entry);
    return entry;
}

/** The stored attribution, or null once it has expired (the entry is then removed). */
export function getAttribution(now = Date.now()): StoredAttribution | null {
    const entry = readStorage();
    if (!entry) return null;
    if (now - entry.at > REF_TTL_MS) {
        writeStorage(null);
        return null;
    }
    return entry;
}

/** The short-link code to send with a public form, or null. */
export function getRef(now = Date.now()): string | null {
    return getAttribution(now)?.ref ?? null;
}

/** `{ ref }` to spread into a form payload — empty when nothing is stored. */
export function attributionPayload(): { ref?: string } {
    const ref = getRef();
    return ref ? { ref } : {};
}

/**
 * Add `ref=<code>` to a path or URL when a code is stored, keeping any
 * query string it already has. Used on the article pages' CTA links so a
 * reader who came from a shared post stays attributed on the next page.
 */
export function appendRef(url: string): string {
    const ref = getRef();
    if (!ref) return url;
    const [base = '', hash = ''] = url.split('#');
    const [path = '', query = ''] = base.split('?');
    const params = new URLSearchParams(query);
    params.set(REF_QUERY_PARAM, ref);
    return `${path}?${params.toString()}${hash ? `#${hash}` : ''}`;
}

export function clearAttribution(): void {
    writeStorage(null);
}
